import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Star, ArrowRight, ShieldCheck, BadgeCheck, HardHat, ExternalLink, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AddressAutocomplete from "@/components/site/AddressAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREFILL_KEY = "bd_prefill_v1";
const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/BIG+D%C3%89M%C3%89NAGEMENT/@48.8800933,2.337217,17z/data=!4m6!3m5!1s0x47e6676f04cfb483:0x6d2b1e27c4d85b54!8m2!3d48.8800933!4d2.337217!16s%2Fg%2F11ry9cnkcv";

/** Intro du hero tronquée à 4 lignes en mobile et 6 en desktop, avec un « Voir plus ». */
function HeroIntro({ className, maxLines, children }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      if (expanded) return;
      setTruncated(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [expanded, maxLines, children]);

  return (
    <div className={className}>
      <p
        ref={ref}
        data-testid="hero-subtext"
        style={
          expanded
            ? undefined
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: maxLines,
                overflow: "hidden",
              }
        }
      >
        {children}
      </p>
      {truncated && (
        <button
          type="button"
          data-testid="hero-subtext-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-semibold text-[#237cb5] hover:text-[#1a5d8a] transition-colors"
        >
          {expanded ? "Voir moins" : "Voir plus"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

export default function Hero({
  eyebrow = "Société de déménagement en Île-de-France",
  h1Prefix = "Entreprise de déménagement en\u00A0",
  h1Accent = <span className="whitespace-nowrap">Île-de-France</span>,
  h1Suffix = " : déménageurs professionnels | devis gratuit",
  subtext,
  imageSrc = "/images/camion-demenagement-paris-tour-eiffel.webp",
  imageAlt = "Camion Big Déménagement devant la Tour Eiffel à Paris",
  formLabel = "Adresse de départ",
  formPlaceholder = "Ex : 50 rue de Londres 75008 Paris",
  formButton = "Devis gratuit",
  formAction = "/demande-de-devis",
  formFields = null, // [{ name, label, type?, placeholder? }, ...] — if provided, overrides the single adresse field
  formFlat = false, // removes blue background & border (renders like mobile version on all breakpoints)
  prefillSource = "devis", // tracks the form of origin for admin segmentation
  trustBadges,
  subtextClassName = "mt-6 sm:mt-9 text-[15px] sm:text-base leading-relaxed text-[#3a4a5a] max-w-xl",
} = {}) {
  const [stats, setStats] = useState({ rating: 4.9, count: 704 });
  const [villeDepart, setVilleDepart] = useState("");
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 639px)").matches;
  });
  const [fieldValues, setFieldValues] = useState(
    formFields ? Object.fromEntries(formFields.map((f) => [f.name, ""])) : {}
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        if (cancelled) return;
        const r = res.data?.rating;
        const c = res.data?.userRatingCount;
        if (r && c) setStats({ rating: r, count: c });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      const payload = formFields
        ? { ...fieldValues, source: prefillSource, ts: Date.now() }
        : { ville_depart: villeDepart.trim(), source: prefillSource, ts: Date.now() };
      sessionStorage.setItem(PREFILL_KEY, JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent("bd:prefill"));
    } catch {
      /* ignore */
    }
    const targetHash = (formAction || "/demande-de-devis").split("#")[1] || "devis";
    const target = document.getElementById(targetHash);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        const nameInput = document.querySelector("[data-testid='input-nom']");
        if (nameInput) nameInput.focus();
      }, 700);
    } else {
      window.location.href = formAction || "/demande-de-devis";
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-20 sm:pt-28 pb-20 sm:pb-28 overflow-hidden bg-white"
    >
      {/* Soft decorative blob */}
      <div
        className="absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
        style={{ background: "#5793d8" }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bd-topo opacity-40" aria-hidden="true" />

      <div className="bd-container relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start bd-fade-up">
        <div className="lg:col-span-7">
        <div
          data-testid="hero-top-google-rating-mobile"
          className="mb-12 sm:mb-9 flex flex-wrap items-center gap-x-2 gap-y-1 leading-tight"
        >
          <svg className="h-[25px] w-[25px] sm:h-[30px] sm:w-[30px] shrink-0" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
            <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
            <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
            <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/>
          </svg>
          <span className="text-[18px] sm:text-[22px] font-bold text-[#0f1d2b]">
            {stats.rating.toFixed(1).replace(".", ",")}/5
          </span>
          <span className="flex" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-[18px] w-[18px] sm:h-[22px] sm:w-[22px]" fill="#FBBC05" stroke="#FBBC05" />
            ))}
          </span>
          <span className="text-[15px] sm:text-[18px] text-[#5b6b7a]">
            <span className="text-[18px] sm:text-[22px] font-black text-[#237cb5]">
              {stats.count.toLocaleString("fr-FR")}
            </span>{" "}
            avis{" "}
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-top-google-rating-mobile-link"
              className="inline-flex items-center gap-0.5 font-bold text-[#237cb5] underline underline-offset-2 hover:text-[#1a5d8a] transition-colors"
              title="Voir tous les avis sur Google"
            >
              Google
              <ExternalLink className="h-3 w-3 opacity-80" />
            </a>
          </span>
        </div>

        <h1
          data-testid="hero-h1"
          className="bd-h2 bd-hero-h1 text-[25px] sm:text-[30px] lg:text-[36px]"
        >
          {h1Prefix}
          <span style={{ color: "#4285F4" }}>{h1Accent}</span>
          {h1Suffix}
        </h1>

        <HeroIntro className={subtextClassName} maxLines={6}>
          {subtext || (
            <>
              Obtenez votre{" "}
              <strong className="font-semibold text-[#0f1d2b]">devis gratuit</strong> en
              2 minutes : déménagement de particuliers, transfert d'entreprise,
              garde-meuble sécurisé et monte-meuble. Une seule équipe de déménageurs
              professionnels pour tout gérer, avec des tarifs transparents partout en
              Île-de-France.
            </>
          )}
        </HeroIntro>

        {/* Mini lead-capture form */}
        <form
          onSubmit={handleSubmit}
          data-testid="hero-quick-form"
          className="mt-8 sm:mt-14 max-w-xl bg-transparent border-0 p-0 shadow-none sm:bg-[#eaf3fb] sm:border sm:border-[#d7e7f5] sm:rounded-2xl sm:p-5"
        >
          {formFields ? (
            <div className="mt-1.5 flex flex-col sm:flex-row sm:items-end sm:justify-center gap-2">
              {formFields.map((f) => {
                const hasIcon = f.name.startsWith("adresse");
                return (
                  <div key={f.name} className="relative w-full sm:w-[400px]">
                    {f.label && (
                      <Label
                        htmlFor={`hero-field-${f.name}`}
                        className="hidden sm:block text-[11.5px] font-bold tracking-[0.14em] text-[#155a87] pl-1 mb-1.5"
                      >
                        {f.label}
                      </Label>
                    )}
                    <div className="relative">
                      {hasIcon && (
                        <MapPin
                          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#237cb5] pointer-events-none"
                          aria-hidden="true"
                        />
                      )}
                      <Input
                        id={`hero-field-${f.name}`}
                        name={f.name}
                        data-testid={`hero-field-${f.name}`}
                        type={f.type || "text"}
                        required
                        value={fieldValues[f.name] || ""}
                        onChange={(e) =>
                          setFieldValues((prev) => ({ ...prev, [f.name]: e.target.value }))
                        }
                        placeholder={isMobile ? (f.mobilePlaceholder || f.label || f.placeholder || "") : (f.placeholder || "")}
                        aria-label={f.label}
                        className={`h-12 text-sm placeholder:text-xs placeholder:text-[#b8c4d1] bg-white border border-[#5b6b7a] sm:border-2 sm:border-[#1c66a0] focus-visible:ring-[#1c66a0]/40 ${hasIcon ? "pl-9" : ""}`}
                      />
                    </div>
                  </div>
                );
              })}
              <button
                type="submit"
                data-testid="hero-quick-submit"
                className="h-12 sm:self-end inline-flex items-center justify-center gap-2 rounded-xl px-6 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
              >
                {formButton}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="mt-1.5 flex flex-col sm:flex-row sm:items-end sm:justify-center gap-2">
              <div className="relative w-full sm:w-[400px]">
                <Label
                  htmlFor="hero-ville-depart"
                  className="hidden sm:inline-block text-[11.5px] font-bold tracking-[0.14em] text-[#155a87] pl-1 mb-1.5"
                >
                  {formLabel}
                </Label>
                <AddressAutocomplete
                  id="hero-ville-depart"
                  data-testid="hero-input-villedepart"
                  value={villeDepart}
                  onChange={setVilleDepart}
                  onSelect={(s) => setVilleDepart(s.description)}
                  placeholder={isMobile ? "Adresse départ" : formPlaceholder}
                  className="h-12 text-sm placeholder:text-xs placeholder:text-[#b8c4d1] bg-white border border-[#5b6b7a] sm:border-2 sm:border-[#1c66a0] focus-visible:ring-[#1c66a0]/40"
                />
              </div>

              <button
                type="submit"
                data-testid="hero-quick-submit"
                className="h-12 sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl px-6 sm:px-8 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
              >
                {formButton}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Trust badges below the button */}
          <div
            data-testid="hero-form-trust-mobile"
            className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
          >
            {(trustBadges || [
              { icon: ShieldCheck, label: "Sans engagement" },
              { icon: HardHat, label: "Déménageurs professionnels" },
            ]).map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0f1d2b]">
                <Icon className="h-4 w-4 text-[#237cb5]" aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>
        </form>
        </div>

        {/* Right column — photo */}
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <div
            data-testid="hero-photo"
            className="relative w-full aspect-square rounded-3xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(35,124,181,0.45)]"
          >
            <picture>
              {imageSrc?.endsWith(".webp") && (
                <source
                  media="(max-width: 768px)"
                  srcSet={imageSrc.replace(".webp", "-mobile.webp")}
                  type="image/webp"
                />
              )}
              <img
                src={imageSrc}
                alt={imageAlt}
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width="1000"
                height="1000"
              />
            </picture>
          </div>
        </div>
      </div>
    </section>
  );
}
